import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { 
  Users, 
  Check, 
  CheckCheck,
  Pin,
  VolumeX
} from 'lucide-react';

interface ChatListItemDesignProps {
  name: string;
  avatar?: string | null;
  lastMessage?: string;
  timestamp?: string;
  unreadCount?: number;
  isSelected?: boolean;
  isOnline?: boolean;
  isGroup?: boolean;
  isPinned?: boolean;
  isMuted?: boolean;
  isRead?: boolean;
  isDelivered?: boolean;
  isCurrentUserMessage?: boolean;
  onClick?: () => void;
}

const ChatListItemDesign: React.FC<ChatListItemDesignProps> = ({
  name,
  avatar,
  lastMessage,
  timestamp,
  unreadCount = 0,
  isSelected = false,
  isOnline = false,
  isGroup = false,
  isPinned = false,
  isMuted = false,
  isRead = false,
  isDelivered = false,
  isCurrentUserMessage = false,
  onClick
}) => {
  return (
    <div
      className={cn(
        "flex items-center space-x-3 p-4 mx-2 rounded-2xl cursor-pointer transition-smooth relative",
        "hover:bg-primary/5 active:bg-primary/10",
        isSelected && "bg-primary/10 shadow-lg shadow-primary/10 ring-1 ring-primary/20",
        isPinned && "bg-gradient-to-r from-secondary/5 to-transparent"
      )}
      onClick={onClick}
    >
      {/* Pin Indicator */}
      {isPinned && (
        <Pin className="absolute top-2 right-2 w-3 h-3 text-secondary" />
      )}

      {/* Avatar with Online Status */}
      <div className="relative shrink-0">
        <Avatar className={cn(
          "h-14 w-14 ring-2 transition-smooth",
          isSelected 
            ? "ring-primary/30" 
            : "ring-border/20"
        )}>
          <AvatarImage src={avatar || ''} />
          <AvatarFallback className={cn(
            "text-lg font-semibold",
            isGroup 
              ? "gradient-secondary text-secondary-foreground" 
              : "bg-muted text-muted-foreground"
          )}>
            {isGroup ? (
              <Users className="w-7 h-7" />
            ) : (
              name[0]?.toUpperCase()
            )}
          </AvatarFallback>
        </Avatar>
        
        {/* Online Status */}
        {!isGroup && isOnline && (
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background"></div>
        )}
        
        {/* Muted Indicator */}
        {isMuted && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-muted rounded-full flex items-center justify-center">
            <VolumeX className="w-3 h-3 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center justify-between">
          <h3 className={cn(
            "font-semibold truncate transition-smooth",
            isSelected ? "text-primary" : "text-foreground",
            unreadCount > 0 && "text-primary"
          )}>
            {name}
          </h3>
          
          <div className="flex items-center space-x-2 shrink-0">
            {timestamp && (
              <span className={cn(
                "text-xs transition-smooth",
                unreadCount > 0 
                  ? "text-primary font-medium" 
                  : "text-muted-foreground"
              )}>
                {timestamp}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            {/* Message Status for Current User */}
            {isCurrentUserMessage && lastMessage && (
              <div className="shrink-0">
                {isRead ? (
                  <CheckCheck className="w-4 h-4 text-primary" />
                ) : isDelivered ? (
                  <CheckCheck className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <Check className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            )}

            {/* Group Badge */}
            {isGroup && (
              <Badge variant="secondary" className="text-xs px-2 py-0.5 shrink-0">
                Gruppo
              </Badge>
            )}
            
            {/* Last Message */}
            {lastMessage && (
              <p className={cn(
                "text-sm truncate transition-smooth",
                unreadCount > 0 
                  ? "text-foreground font-medium" 
                  : "text-muted-foreground"
              )}>
                {lastMessage}
              </p>
            )}
          </div>

          {/* Unread Badge */}
          {unreadCount > 0 && (
            <Badge className="gradient-primary text-primary-foreground text-xs min-w-[20px] h-5 rounded-full flex items-center justify-center font-bold">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatListItemDesign;